import React, { useMemo, useEffect, useState } from "react";
import { useFormik } from 'formik';
import {
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    InputAdornment,
    Button,
    FormHelperText,
    Dialog,
    DialogTitle,
    DialogContent,
    CircularProgress
} from '@mui/material';
import { patientSchema } from '../../validations/schemas';
import DoctorSearchAndRegister from '../../components/doctors/DoctorSearchAndRegister';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { doctorAPI } from '../../services/api';

const PatientFormDialog = ({ open, onClose, patient, onSubmit }) => {
    const { t } = useTranslation();
    const [doctorData, setDoctorData] = useState(null);
    const [loadingDoctor, setLoadingDoctor] = useState(false);
    const [formKey, setFormKey] = useState(0);

    // Fetch Doctor Data if needed
    useEffect(() => {
        const fetchDoctorData = async () => {
            setDoctorData(null);

            if (!patient?.doctorReferred) {
                return;
            }

            // Case 1: Already populated object
            if (
                typeof patient.doctorReferred === 'object' &&
                patient.doctorReferred !== null &&
                patient.doctorReferred._id &&
                patient.doctorReferred.name
            ) {
                setDoctorData(patient.doctorReferred);
                return;
            }

            // Case 2: Just an ID string - fetch the doctor
            const doctorId = typeof patient.doctorReferred === 'string'
                ? patient.doctorReferred
                : patient.doctorReferred?._id;

            if (doctorId) {
                setLoadingDoctor(true);
                try {
                    //   Use the fixed getOne method
                    const response = await doctorAPI.getOne(doctorId);

                    let doctor = null;
                    if (response.data?.data) {
                        doctor = Array.isArray(response.data.data)
                            ? response.data.data[0]
                            : response.data.data;
                    } else if (response.data) {
                        doctor = response.data;
                    }

                    if (doctor && doctor._id) {
                        setDoctorData(doctor);
                    }
                } catch (error) {
                    console.error('Failed to fetch doctor:', error);
                    toast.error(t('patients.errorLoadingDoctor', 'Failed to load doctor information'));
                } finally {
                    setLoadingDoctor(false);
                }
            }
        };

        if (open && patient) {
            fetchDoctorData();
        } else if (open && !patient) {
            setDoctorData(null);
        }
    }, [open, patient, t]);

    // Reset when dialog closes
    useEffect(() => {
        if (!open) {
            setDoctorData(null);
            setLoadingDoctor(false);
        }
    }, [open]);

    // Force form re-render when doctor data loads
    useEffect(() => {
        if (doctorData) {
            setFormKey(prev => prev + 1);
        }
    }, [doctorData]);

    // Build Initial Values
    const initialValues = useMemo(() => {
        if (patient) {
            let doctorValue = null;

            if (doctorData && doctorData._id) {
                doctorValue = doctorData;
            } else if (
                typeof patient.doctorReferred === 'object' &&
                patient.doctorReferred !== null &&
                patient.doctorReferred._id &&
                patient.doctorReferred.name
            ) {
                doctorValue = patient.doctorReferred;
            }

            return {
                name: patient.name || '',
                age: patient.age || '',
                gender: patient.gender || '',
                phoneNumber: patient.phoneNumber?.startsWith('+20')
                    ? patient.phoneNumber.substring(3)
                    : (patient.phoneNumber || ''),
                socialNumber: patient.socialNumber || '',
                address: patient.address || '',
                doctorReferred: doctorValue,
            };
        }

        return {
            name: '',
            age: '',
            gender: '',
            phoneNumber: '',
            socialNumber: '',
            address: '',
            doctorReferred: null
        };
    }, [patient, doctorData]);

    // Form Submit Handler
    const handleFormSubmit = async (payload) => {
        const isEditMode = Boolean(patient?._id);
        await onSubmit(payload, isEditMode ? patient._id : null, isEditMode ? 'edit' : 'create');
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {patient ? t('patients.editPatient', 'Edit Patient') : t('patients.addPatient', 'Add Patient')}
            </DialogTitle>
            <DialogContent>
                {loadingDoctor ? (
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        minHeight={300}
                        gap={2}
                    >
                        <CircularProgress />
                        <Typography variant="body2" color="text.secondary">
                            {t('common.loading', 'Loading...')}
                        </Typography>
                    </Box>
                ) : (
                    <PatientForm
                        key={`patient-form-${patient?._id || 'new'}-${formKey}`}
                        initialValues={initialValues}
                        onSubmit={handleFormSubmit}
                        onCancel={onClose}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

const PatientForm = ({
    initialValues,
    onCancel,
    onSubmit,
    submitButtonText = null,
    showCancelButton = true,
}) => {
    const { t } = useTranslation();

    const formik = useFormik({
        initialValues,
        validationSchema: patientSchema,
        enableReinitialize: true,
        onSubmit: async (values, formikHelpers) => {
            const { setSubmitting, setErrors } = formikHelpers;
            setSubmitting(true);

            const payload = {
                ...values,
                doctorReferred: values.doctorReferred?._id || values.doctorReferred || null,
                phoneNumber: values.phoneNumber ? `+20${values.phoneNumber}` : '',
            };

            try {
                await onSubmit(payload);
            } catch (error) {
                const errorMessage = error.response?.data?.message || error.message || t('common.unexpectedError', 'An unexpected error occurred');
                setErrors({ submit: errorMessage });
                toast.error(errorMessage);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const { isSubmitting, errors } = formik;

    return (
        <Box component="form" onSubmit={formik.handleSubmit} noValidate sx={{ mt: 2 }}>
            {errors.submit && (
                <Typography color="error" align="center" sx={{ mb: 2 }}>
                    {errors.submit}
                </Typography>
            )}

            <Typography variant="h6" gutterBottom>
                {t('patients.patientDetails', 'Patient Details')}
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label={t('patients.name', 'Name')}
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t('patients.age', 'Age')}
                        name="age"
                        type="number"
                        value={formik.values.age}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.age && Boolean(formik.errors.age)}
                        helperText={formik.touched.age && formik.errors.age}
                        inputProps={{ min: 0, max: 150 }}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth error={formik.touched.gender && Boolean(formik.errors.gender)}>
                        <InputLabel>{t('patients.gender', 'Gender')}</InputLabel>
                        <Select
                            name="gender"
                            value={formik.values.gender}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            label={t('patients.gender', 'Gender')}
                        >
                            <MenuItem value="male">{t('patients.genders.male', 'Male')}</MenuItem>
                            <MenuItem value="female">{t('patients.genders.female', 'Female')}</MenuItem>
                            <MenuItem value="other">{t('patients.genders.other', 'Other')}</MenuItem>
                        </Select>
                        {formik.touched.gender && formik.errors.gender && (
                            <FormHelperText>{formik.errors.gender}</FormHelperText>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t('patients.phoneNumber', 'Phone Number')}
                        name="phoneNumber"
                        value={formik.values.phoneNumber}
                        onChange={e => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                            formik.setFieldValue('phoneNumber', val);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.phoneNumber && Boolean(formik.errors.phoneNumber)}
                        helperText={formik.touched.phoneNumber && formik.errors.phoneNumber}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">+20</InputAdornment>,
                            inputProps: { maxLength: 10, pattern: '[0-9]{10}' }
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label={t('patients.socialNumber', 'Social Number')}
                        name="socialNumber"
                        value={formik.values.socialNumber || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.socialNumber && Boolean(formik.errors.socialNumber)}
                        helperText={formik.touched.socialNumber && formik.errors.socialNumber}
                        placeholder={t('patients.socialNumberPlaceholder', 'Enter social number')}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField
                        fullWidth
                        label={t('patients.address', 'Address')}
                        name="address"
                        value={formik.values.address || ''}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.address && Boolean(formik.errors.address)}
                        helperText={formik.touched.address && formik.errors.address}
                        multiline
                        rows={2}
                        placeholder={t('patients.addressPlaceholder', 'Enter full address')}
                    />
                </Grid>

                <Grid item xs={12}>
                    <DoctorSearchAndRegister
                        value={formik.values.doctorReferred}
                        onChange={(doctor) => formik.setFieldValue('doctorReferred', doctor)}
                        error={formik.touched.doctorReferred && Boolean(formik.errors.doctorReferred)}
                        helperText={formik.touched.doctorReferred && formik.errors.doctorReferred?.toString()}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                        {showCancelButton && (
                            <Button
                                onClick={onCancel}
                                color="secondary"
                                variant="outlined"
                                disabled={isSubmitting}
                            >
                                {t('common.cancel', 'Cancel')}
                            </Button>
                        )}
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting
                                ? t('common.saving', 'Saving...')
                                : (submitButtonText || t('common.save', 'Save'))
                            }
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default PatientFormDialog;